/**
 * WizardStepUnits (Development Project Step 4)
 *
 * Sets up unit count and naming scheme for development projects.
 * - Total number of units/lots
 * - Unit label prefix (Lot, Unit, Building, Townhome)
 * - Starting number
 * - Preview of generated unit labels
 */

import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Hash, Tag } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { WizardProjectData } from '@/types/wizard';

interface WizardStepUnitsProps {
  data: WizardProjectData;
  onUpdate: (data: Partial<WizardProjectData>) => void;
}

const UNIT_LABEL_OPTIONS = [
  { value: 'Lot', label: 'Lot', description: 'Lot 1, Lot 2, etc.' },
  { value: 'Unit', label: 'Unit', description: 'Unit 1, Unit 2, etc.' },
  { value: 'Building', label: 'Building', description: 'Building 1, Building 2, etc.' },
  { value: 'Townhome', label: 'Townhome', description: 'Townhome 1, Townhome 2, etc.' },
  { value: 'Space', label: 'Space', description: 'Space 1, Space 2, etc.' },
];

export function WizardStepUnits({ data, onUpdate }: WizardStepUnitsProps) {
  // Generate preview of unit labels
  const unitPreview = useMemo(() => {
    const units: string[] = [];
    const maxPreview = Math.min(data.totalUnits, 6);
    const remaining = data.totalUnits - maxPreview;

    for (let i = 0; i < maxPreview; i++) {
      const number = data.unitStartNumber + i;
      units.push(`${data.unitLabelPrefix} ${number}`);
    }

    return { units, remaining };
  }, [data.totalUnits, data.unitLabelPrefix, data.unitStartNumber]);

  // Get development type label
  const getDevelopmentTypeLabel = () => {
    const labels: Record<string, string> = {
      subdivision: 'subdivision',
      townhome: 'townhome community',
      apartment: 'apartment complex',
      mixed_use: 'mixed-use development',
      commercial_complex: 'commercial complex',
    };
    return data.developmentType ? labels[data.developmentType] || 'development' : 'development';
  };

  return (
    <div className="space-y-6">
      {/* Question */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">How many units?</h2>
        <p className="text-muted-foreground">
          Define the units in your {getDevelopmentTypeLabel()}
        </p>
      </div>

      {/* Unit Count */}
      <div className="space-y-2">
        <Label htmlFor="totalUnits" className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          Total Number of Units *
        </Label>
        <Input
          id="totalUnits"
          type="number"
          min={1}
          max={1000}
          value={data.totalUnits}
          onChange={(e) => onUpdate({ totalUnits: Math.max(1, parseInt(e.target.value) || 1) })}
          className="max-w-[200px]"
        />
        <p className="text-sm text-muted-foreground">
          You can add or modify units later from the project page
        </p>
      </div>

      {/* Unit Label Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Unit Labeling
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Label Prefix */}
          <div className="space-y-2">
            <Label htmlFor="unitLabelPrefix">Label Type</Label>
            <Select
              value={data.unitLabelPrefix}
              onValueChange={(value) => onUpdate({ unitLabelPrefix: value })}
            >
              <SelectTrigger className="max-w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {UNIT_LABEL_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <span>{option.label}</span>
                      <span className="text-muted-foreground text-xs">
                        ({option.description})
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Start Number */}
          <div className="space-y-2">
            <Label htmlFor="unitStartNumber" className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              Starting Number
            </Label>
            <Input
              id="unitStartNumber"
              type="number"
              min={1}
              value={data.unitStartNumber}
              onChange={(e) =>
                onUpdate({ unitStartNumber: Math.max(1, parseInt(e.target.value) || 1) })
              }
              className="max-w-[120px]"
            />
            <p className="text-sm text-muted-foreground">
              First unit will be labeled "{data.unitLabelPrefix} {data.unitStartNumber}"
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Unit Preview */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {unitPreview.units.map((label, index) => (
              <Badge key={index} variant="secondary" className="text-sm py-1 px-3">
                {label}
              </Badge>
            ))}
            {unitPreview.remaining > 0 && (
              <Badge variant="outline" className="text-sm py-1 px-3 text-muted-foreground">
                +{unitPreview.remaining} more
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            {data.totalUnits} {data.totalUnits === 1 ? 'unit' : 'units'} will be created:{' '}
            {data.unitLabelPrefix} {data.unitStartNumber}
            {data.totalUnits > 1 && ` through ${data.unitLabelPrefix} ${data.unitStartNumber + data.totalUnits - 1}`}
          </p>
        </CardContent>
      </Card>

      {/* Help text */}
      <p className="text-center text-sm text-muted-foreground">
        Units can have individual addresses and permit requirements added later.
      </p>
    </div>
  );
}
